/**
 * Case-study content, shared by the Vue app (webpack) and the build scripts
 * (Node), so both read the same data. Plain CommonJS on purpose.
 *
 *   case-studies.en.json   canonical entries: published, relatedProductSlugs
 *                          and the English copy
 *   case-studies.ar.json   the Arabic copy for the same slugs (and its own
 *                          page / label strings)
 *
 * A study is LIVE (rendered, prerendered, linked) only when its `published`
 * flag is true AND neither language contains a [[placeholder]]. The check in
 * scripts/check-prerender.js fails if a study marked published still has one.
 *
 * Whether the whole section is shown (nav, sitemap, indexing) is a separate
 * switch: src/config/features.json -> showCaseStudies.
 */
const en = require('./case-studies.en.json')
const ar = require('./case-studies.ar.json')

const PLACEHOLDER = /\[\[[^\]]*\]\]/

const COPY = { en, ar }

/** Every string in a value (deep), for placeholder scanning. */
function strings (value) {
  if (typeof value === 'string') {
    return [value]
  }
  if (Array.isArray(value)) {
    return value.flatMap(strings)
  }
  if (value && typeof value === 'object') {
    return Object.values(value).flatMap(strings)
  }
  return []
}

const hasPlaceholder = (value) => strings(value).some((text) => PLACEHOLDER.test(text))

const arabicFor = (slug) => ar.studies.find((study) => study.slug === slug)

/** A study is live when it is published, complete and has an Arabic twin. */
function isLive (study) {
  const twin = arabicFor(study.slug)
  return study.published === true && Boolean(twin) && !hasPlaceholder(study) && !hasPlaceholder(twin)
}

/** The English entry, or the Arabic copy laid over it (the canonical fields stay English). */
function merge (study, locale) {
  if (locale !== 'ar') {
    return study
  }
  const twin = arabicFor(study.slug)
  return {
    ...study,
    ...twin,
    published: study.published,
    relatedProductSlugs: study.relatedProductSlugs,
    metrics: study.metrics.map((metric, index) => ({ value: metric.value, label: twin.metrics?.[index]?.label || metric.label }))
  }
}

/** Live studies in a locale, in file order. */
function localised (locale) {
  return en.studies.filter(isLive).map((study) => merge(study, locale))
}

const find = (slug, locale) => localised(locale).find((study) => study.slug === slug)

const copy = (locale) => COPY[locale] || en
const labels = (locale) => ({ ...en.labels, ...copy(locale).labels })
const page = (locale) => ({ ...en.page, ...copy(locale).page })

/** Title, description and Article data for a study, built only from its own fields. */
function seo (study, brand) {
  const description = [study.clientLabel, study.industry].filter(Boolean).join(' · ')
  return {
    title: `${study.title} | ${brand}`,
    description: `${description}. ${study.result}`
  }
}

/** Everything wrong with the content files (empty when they are healthy). */
function validate (solutionSlugs = []) {
  const problems = []
  const seen = new Set()
  for (const study of en.studies) {
    const where = `case study "${study.slug}"`
    if (seen.has(study.slug)) {
      problems.push(`${where}: duplicate slug`)
    }
    seen.add(study.slug)
    const twin = arabicFor(study.slug)
    if (!twin) {
      problems.push(`${where}: no Arabic entry in case-studies.ar.json`)
    }
    for (const field of ['title', 'clientLabel', 'industry', 'problem', 'result']) {
      if (!study[field]) {
        problems.push(`${where}: "${field}" is empty`)
      }
      if (twin && !twin[field]) {
        problems.push(`${where}: Arabic "${field}" is empty`)
      }
    }
    if (!study.solution?.length) {
      problems.push(`${where}: "solution" has no bullets`)
    }
    if (twin && twin.solution?.length !== study.solution?.length) {
      problems.push(`${where}: Arabic "solution" has ${twin.solution?.length || 0} bullets, English has ${study.solution?.length || 0}`)
    }
    if (twin && (twin.metrics || []).length && twin.metrics.length !== study.metrics.length) {
      problems.push(`${where}: Arabic "metrics" does not match the English metrics`)
    }
    for (const slug of study.relatedProductSlugs || []) {
      if (solutionSlugs.length && !solutionSlugs.includes(slug)) {
        problems.push(`${where}: relatedProductSlugs "${slug}" is not a product slug`)
      }
    }
    // Published but still carrying a [[placeholder]]: the site hides it, but it must be fixed or unpublished.
    if (study.published === true && (hasPlaceholder(study) || hasPlaceholder(twin))) {
      problems.push(`${where}: is published but still contains a [[placeholder]] (finish the copy in both languages, or set published to false)`)
    }
  }
  for (const twin of ar.studies) {
    if (!seen.has(twin.slug)) {
      problems.push(`Arabic case study "${twin.slug}" has no English entry`)
    }
  }
  return problems
}

module.exports = {
  PLACEHOLDER,
  allStudies: en.studies,
  hasPlaceholder,
  isLive,
  localised,
  find,
  labels,
  page,
  seo,
  validate
}
